import React, { useRef, useEffect, useCallback, useState } from 'react';
import RichTextSlideRenderer from './RichTextSlideRenderer';
import ImageEmbedDialog from './ImageEmbedDialog';
import { buildImageHtml } from '../utils/imageEmbedSizes';
import { supabase } from '../services/supabaseClient';

const TOOLBAR_BTN = {
    padding: '4px 9px',
    border: '1px solid #e2e8f0',
    background: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 700,
    outline: 'none',
    color: '#334155',
    minWidth: '28px',
};

function exec(cmd, val = null) {
    document.execCommand('styleWithCSS', false, false);
    document.execCommand(cmd, false, val);
}

/**
 * Rich-text editor for exercise Answers / explanations (shown to students when allowed).
 * Same formatting as Teacher Notes, plus image insert / paste.
 */
export default function AnswersEditor({ value = '', onChange, placeholder }) {
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const isFocusedRef = useRef(false);
    const lastEmittedRef = useRef(value || '');
    const savedSelectionRef = useRef(null);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);

    useEffect(() => {
        const el = editorRef.current;
        if (!el || isFocusedRef.current) return;
        const next = value || '';
        if (el.innerHTML !== next) {
            el.innerHTML = next;
        }
        lastEmittedRef.current = next;
    }, [value]);

    const emitChange = useCallback(() => {
        const html = editorRef.current?.innerHTML || '';
        lastEmittedRef.current = html;
        onChange?.(html);
    }, [onChange]);

    const saveSelection = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        const el = editorRef.current;
        if (!el || !el.contains(range.commonAncestorContainer)) return;
        savedSelectionRef.current = range.cloneRange();
    };

    const restoreSelection = () => {
        const el = editorRef.current;
        const range = savedSelectionRef.current;
        if (!el || !range) return;
        el.focus();
        const sel = window.getSelection();
        if (!sel) return;
        sel.removeAllRanges();
        sel.addRange(range);
    };

    const run = (cmd, val = null) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        restoreSelection();
        exec(cmd, val);
        emitChange();
        saveSelection();
    };

    const insertImageHtml = (signedUrl, widthPercent) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        restoreSelection();
        exec('insertHTML', buildImageHtml(signedUrl, widthPercent));
        emitChange();
        saveSelection();
    };

    const uploadAndInsertFile = async (file) => {
        if (!file || !String(file.type || '').startsWith('image/')) return;
        try {
            const fileExt = (file.name || 'png').split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
            const filePath = `exercises/answers/${fileName}`;
            const { error } = await supabase.storage.from('media').upload(filePath, file);
            if (error) throw error;
            const { data: { signedUrl } } = await supabase.storage
                .from('media')
                .createSignedUrl(filePath, 31536000);
            if (signedUrl) insertImageHtml(signedUrl);
        } catch (err) {
            console.error('[AnswersEditor] image upload failed:', err);
            alert('Failed to upload image: ' + (err?.message || 'Unknown error'));
        }
    };

    const clearFormatting = () => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        restoreSelection();
        const sel = window.getSelection();
        let node = sel?.anchorNode;
        if (node?.nodeType === 3) node = node.parentElement;
        if (node?.closest?.('ul')) document.execCommand('insertUnorderedList', false, null);
        if (node?.closest?.('ol')) document.execCommand('insertOrderedList', false, null);
        exec('removeFormat');
        exec('unlink');
        emitChange();
        saveSelection();
    };

    const handlePaste = async (e) => {
        e.stopPropagation();
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of items) {
            if (item.type?.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (file) await uploadAndInsertFile(file);
                return;
            }
        }
        // Non-image: allow default paste so Word/Docs formatting is preserved.
    };

    return (
        <div className="answers-editor teacher-notes-editor" style={{ border: '1.5px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', background: '#f0fdf4' }}>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    padding: '6px 8px',
                    background: '#dcfce7',
                    borderBottom: '1px solid #bbf7d0',
                    alignItems: 'center',
                }}
            >
                <button type="button" style={TOOLBAR_BTN} onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => run('bold')} title="Bold">
                    <strong>B</strong>
                </button>
                <button type="button" style={TOOLBAR_BTN} onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => run('italic')} title="Italic">
                    <em>I</em>
                </button>
                <button type="button" style={TOOLBAR_BTN} onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => run('underline')} title="Underline">
                    <span style={{ textDecoration: 'underline' }}>U</span>
                </button>
                <div style={{ width: 1, alignSelf: 'stretch', background: '#bbf7d0', margin: '0 2px' }} />
                <button type="button" style={TOOLBAR_BTN} onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => run('insertUnorderedList')} title="Bullet list">
                    • List
                </button>
                <button type="button" style={TOOLBAR_BTN} onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => run('insertOrderedList')} title="Numbered list">
                    1. List
                </button>
                <div style={{ width: 1, alignSelf: 'stretch', background: '#bbf7d0', margin: '0 2px' }} />
                <button
                    type="button"
                    style={TOOLBAR_BTN}
                    onMouseDown={e => { e.preventDefault(); saveSelection(); }}
                    onClick={() => setImageDialogOpen(true)}
                    title="Insert image"
                >
                    🖼
                </button>
                <button
                    type="button"
                    style={TOOLBAR_BTN}
                    onMouseDown={e => { e.preventDefault(); saveSelection(); }}
                    onClick={() => fileInputRef.current?.click()}
                    title="Upload image"
                >
                    Upload
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadAndInsertFile(file);
                        e.target.value = '';
                    }}
                />
                <div style={{ width: 1, alignSelf: 'stretch', background: '#bbf7d0', margin: '0 2px' }} />
                <button type="button" style={TOOLBAR_BTN} onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={clearFormatting} title="Clear formatting">
                    ✕
                </button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder={placeholder}
                onFocus={() => { isFocusedRef.current = true; }}
                onBlur={() => {
                    isFocusedRef.current = false;
                    emitChange();
                }}
                onMouseUp={saveSelection}
                onKeyUp={saveSelection}
                onInput={emitChange}
                onPaste={handlePaste}
                style={{
                    minHeight: '100px',
                    maxHeight: '320px',
                    overflowY: 'auto',
                    padding: '0.65rem 0.8rem',
                    outline: 'none',
                    fontSize: '0.88rem',
                    lineHeight: 1.55,
                    color: '#14532d',
                    fontFamily: 'inherit',
                }}
            />
            <ImageEmbedDialog
                isOpen={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                onImageSelected={(signedUrl, { widthPercent } = {}) => {
                    insertImageHtml(signedUrl, widthPercent);
                }}
                folderPrefix="exercises/answers/"
            />
            <style>{`
                .answers-editor ul {
                    list-style: disc !important;
                    padding-left: 1.5em !important;
                    margin: 0.35em 0 !important;
                }
                .answers-editor ol {
                    list-style: decimal !important;
                    padding-left: 1.5em !important;
                    margin: 0.35em 0 !important;
                }
                .answers-editor li {
                    list-style: inherit !important;
                    margin: 0.15em 0;
                }
                .answers-editor em,
                .answers-editor i {
                    font-style: italic !important;
                }
                .answers-editor strong,
                .answers-editor b {
                    font-weight: 700 !important;
                }
                .answers-editor u {
                    text-decoration: underline !important;
                }
                .answers-editor img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 8px;
                    margin: 0.4rem 0;
                }
                .answers-editor [data-placeholder]:empty:before {
                    content: attr(data-placeholder);
                    color: #16a34a;
                    opacity: 0.55;
                    pointer-events: none;
                }
            `}</style>
        </div>
    );
}

/** Read-only Answers display (student end-of-exercise or teacher panel). */
export function AnswersView({ html, style = {}, emptyLabel = 'No answers yet.', title = 'Answers' }) {
    const trimmed = (html || '').replace(/<br\s*\/?>/gi, '').replace(/&nbsp;/g, '').trim();
    const hasContent = trimmed.length > 0 && trimmed !== '<p></p>' && trimmed !== '<div><br></div>';

    if (!hasContent) {
        return (
            <div style={{ fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic', ...style }}>
                {emptyLabel}
            </div>
        );
    }

    return (
        <div
            className="answers-view answers-editor"
            style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '10px',
                padding: '0.65rem 0.85rem',
                ...style,
            }}
        >
            {title ? (
                <div style={{
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    color: '#166534',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: '0.35rem',
                }}>
                    {title}
                </div>
            ) : null}
            <RichTextSlideRenderer content={html} style={{ fontSize: '0.88rem', lineHeight: 1.55, color: '#14532d' }} />
        </div>
    );
}
